import React, { useCallback, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TabBar } from "@/components/TabBar";
import { KinlyOrb } from "@/components/KinlyOrb";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { usePullToRefresh, PullIndicator } from "@/lib/usePullToRefresh";

/**
 * AppShell — persistent canvas for the 5 surfaces.
 *
 * Layout model:
 *   - kinly-canvas is `position: relative; height: 100dvh; overflow: hidden`.
 *   - Outlet renders into a default scroll container (used by Today, Plan,
 *     Remember, You — non-typing surfaces).
 *   - Typing surfaces (Talk) opt out by rendering `absolute inset-0` content
 *     and using <KeyboardAwareScreen> + <Composer>. They cover the default
 *     scroll area completely.
 *   - TabBar is `position: fixed; bottom: 0` and hides automatically when
 *     the keyboard is open (CSS `[data-kb="open"] .hide-when-kb`).
 *   - The Kinly orb is hidden on Talk surfaces AND when the keyboard is open.
 *
 * Pull-to-refresh:
 *   Owned at this level so every shell-mounted screen (Today, Plan, Remember,
 *   You, Recipes, ProfileSetup) gets it for free. Each screen registers its
 *   refresh handler via outlet context: `useOutletContext().registerRefresh(fn)`.
 *   If no screen has registered, the gesture is a no-op (no crash, no fake
 *   "refreshing" spinner). Talk opts out by setting `absolute inset-0` content
 *   that intercepts touch events before they reach the scroll container.
 */
export function AppShell() {
  const { family } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orbDrafts] = useState(0);
  // Surfaces that hide the floating Kinly orb. Talk surface already has
  // its own composer + AI affordances (the orb would be redundant). The
  // Whereabouts map is intentionally full-screen and tap-on-pin driven —
  // the orange orb covers the bottom-right map area and competes for
  // attention with member pins (per user feedback Feb 2026).
  const orbHidden = location.pathname.startsWith("/talk")
    || location.pathname.startsWith("/whereabouts");

  // Refresh handler registry — child screens push their reload fn here.
  const refreshHandler = useRef(null);
  const registerRefresh = useCallback((fn) => { refreshHandler.current = fn; }, []);
  const onRefresh = useCallback(async () => {
    if (refreshHandler.current) await refreshHandler.current();
  }, []);
  const ptr = usePullToRefresh(onRefresh);

  return (
    <div
      className="kinly-canvas"
      style={{
        // Safe-area insets — pushes content clear of the iPhone notch and
        // home indicator without leaving plain coloured strips above /
        // below the UI. Pairs with StatusBar.overlaysWebView=true so the
        // WebView draws edge-to-edge and we control the inset in CSS.
      }}
    >
      <div
        {...ptr.bind}
        className="absolute inset-0 overflow-y-auto"
        style={{
          ...(ptr.bind.style || {}),
          // Reserve room for tab bar (52) + safe inset. Talk surface
          // overrides this with its own absolute-positioned content.
          paddingBottom: "calc(52px + env(safe-area-inset-bottom))",
        }}
        data-testid="surface-scroll-area"
      >
        <PullIndicator distance={ptr.distance} refreshing={ptr.refreshing} />
        {/* Per-tab error boundary so a crash on (say) /remember doesn't
            also unmount the TabBar — the user can still tap another tab
            to escape the boundary instead of being forced to sign-out
            or force-close the app. */}
        <AppErrorBoundary>
          <Outlet context={{ family, registerRefresh }} />
        </AppErrorBoundary>
      </div>
      {!orbHidden && (
        <div className="hide-when-kb">
          <KinlyOrb hasDrafts={orbDrafts > 0} onClick={() => navigate("/talk?thread=kinly")} />
        </div>
      )}
      <TabBar />
    </div>
  );
}
